import React, { useState, useEffect, useCallback } from 'react';
import { Filter, RefreshCw, Clock, X, CheckCircle, Users, Loader } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { dashboardApi, fetchApiDirect } from '../services/api';
import { message } from 'antd';
import DateRangeFilter from '../components/DateRangeFilter';

// ── Stat Cards ───────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, percentage, color }) => {
  const hexMatch = color.match(/#([A-Fa-f0-9]+)/);
  const hex = hexMatch ? `#${hexMatch[1]}` : '#4B9EF9';

  return (
    <div
      className="rounded-[24px] px-5 py-5 border shadow-sm flex flex-col justify-between min-h-[120px] transition-all hover:scale-[1.02] active:scale-95 group"
      style={{
        backgroundColor: `${hex}1A`,
        borderColor: `${hex}4D`,
        boxShadow: `0 10px 20px -10px ${hex}44`
      }}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col">
          <span className="text-[10px] font-black text-gray-500/70 uppercase tracking-[0.1em] mb-1.5 leading-tight">{label}</span>
          <span className="text-[32px] font-black text-gray-800 tracking-tight leading-none">{value}</span>
        </div>
        <div
          className="p-3.5 rounded-[18px] flex items-center justify-center shrink-0 shadow-lg shadow-black/10 transition-transform group-hover:rotate-6"
          style={{ backgroundColor: hex }}
        >
          <Icon size={26} className="text-white" strokeWidth={3} />
        </div>
      </div>
      {percentage !== undefined && (
        <div className="mt-2 flex items-center gap-1.5">
          <span className="text-[14px] font-black text-gray-700">{percentage}%</span>
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Conversion</span>
        </div>
      )}
    </div>
  );
};

const MiniStatCard = ({ label, value, percentage, color }) => {
  const hexMatch = color.match(/#([A-Fa-f0-9]+)/);
  const hex = hexMatch ? `#${hexMatch[1]}` : '#78909C';

  return (
    <div
      className="rounded-[16px] p-4 border shadow-sm flex flex-col justify-between min-h-[100px] transition-all hover:shadow-md"
      style={{
        backgroundColor: `${hex}1A`,
        borderColor: `${hex}4D`,
        boxShadow: `0 4px 12px -6px ${hex}66`
      }}
    >
      <div>
        <span className="text-[9px] font-black text-gray-500/80 uppercase tracking-widest block mb-2">{label}</span>
        <span className="text-[20px] font-black leading-none block mb-2 text-gray-800 tracking-tight">{value}</span>
        <div className="text-[11px] font-black opacity-80" style={{ color: hex }}>{percentage}%</div>
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();

function DashboardLead() {
  // ── State ────────────────────────────────────────────────────────────────
  const [summary,      setSummary]      = useState(null);
  const [breakdown,    setBreakdown]    = useState(null);
  const [conversion,   setConversion]   = useState(null);
  const [bySource,     setBySource]     = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);
  const [loading,      setLoading]      = useState(true);

  const getTodayDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const todayStr = getTodayDateString();

  const [isFilterOpen,  setIsFilterOpen]  = useState(false);
  const [activeFilters, setActiveFilters] = useState({ startDate: todayStr, endDate: todayStr, floorId: '' });
  const [tempFilters,   setTempFilters]   = useState({ startDate: todayStr, endDate: todayStr, floorId: '' });

  // ── Floors from API (dynamic) ─────────────────────────────────────────────
  const [floors,        setFloors]        = useState([]);
  const [floorsLoading, setFloorsLoading] = useState(false);

  useEffect(() => {
    const loadFloors = async () => {
      setFloorsLoading(true);
      try {
        const res = await fetchApiDirect('/floors');
        const arr = Array.isArray(res)          ? res
                  : Array.isArray(res?.content)  ? res.content
                  : Array.isArray(res?.data)      ? res.data
                  : Array.isArray(res?.floors)    ? res.floors
                  : [];
        setFloors(arr.map(f => ({
          id:   String(f.id || f.floorId || ''),
          name: f.floorName || f.name || `Floor ${f.id || ''}`,
        })).filter(f => f.id));
      } catch (err) {
        console.error('Floors fetch error:', err);
      } finally {
        setFloorsLoading(false);
      }
    };
    loadFloors();
  }, []);

  // ── Fetch dashboard data ──────────────────────────────────────────────────
  const fetchAll = useCallback(async (filters = {}) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate)   params.append('endDate',   filters.endDate);
      if (filters.floorId)   params.append('floorId',   filters.floorId);
      const q = params.toString() ? `?${params}` : '';

      const [sum, brk, conv, src, ordSum] = await Promise.all([
        dashboardApi.getLeadsSummary(q),
        dashboardApi.getLeadsStatusBreakdown(q),
        dashboardApi.getLeadsConversionSummary(q),
        dashboardApi.getLeadsBySource(q),
        dashboardApi.getOrderSummary(q),
      ]);

      setSummary(sum);
      setBreakdown(brk);
      setConversion(conv);
      setBySource(src);
      setOrderSummary(ordSum);
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(activeFilters); }, [activeFilters]); // eslint-disable-line

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleApply = () => {
    setActiveFilters(tempFilters);
    setIsFilterOpen(false);
  };

  const handleReset = () => {
    const defaultFilters = { startDate: todayStr, endDate: todayStr, floorId: '' };
    setTempFilters(defaultFilters);
    setActiveFilters(defaultFilters);
    setIsFilterOpen(false);
  };

  // ── Derived — Summary cards ───────────────────────────────────────────────
  const mainStats = [
    { label: 'Total Leads',         value: fmt(breakdown?.totalLeads),      color: 'bg-[#4B9EF9]', icon: Users },
    { label: 'New Leads',           value: fmt(breakdown?.newLead),          percentage: breakdown?.newLeadPercentage      ?? 0, color: 'bg-[#FF914D]', icon: RefreshCw },
    { label: 'Not Assigned Leads',  value: fmt(breakdown?.notAssigned),      percentage: breakdown?.notAssignedPercentage  ?? 0, color: 'bg-[#FFBD59]', icon: Clock },
    { label: 'Non-Converted Leads', value: fmt(breakdown?.nonConverted),     percentage: breakdown?.nonConvertedPercentage ?? 0, color: 'bg-[#F87171]', icon: X },
    { label: 'Order Placed',        value: fmt(breakdown?.orderPlaced),      percentage: breakdown?.orderPlacedPercentage  ?? 0, color: 'bg-[#14B8A6]', icon: CheckCircle },
  ];

  const detailedStats = Array.isArray(breakdown?.dispositionBreakdown)
    ? breakdown.dispositionBreakdown.map((item) => {
        const colorMap = {
          newLead:          'bg-[#4B9EF9]',
          orderPlaced:      'bg-[#10B981]',
          callBack:         'bg-[#F59E0B]',
          notConnected:     'bg-[#64748B]',
          callDisconnected: 'bg-[#3B82F6]',
          notInterested:    'bg-[#EF4444]',
          futureCallBack:   'bg-[#06B6D4]',
          prankCall:        'bg-[#8B5CF6]',
          switchOff:        'bg-[#F43F5E]',
          didNotOrder:      'bg-[#FF5757]',
          interested:       'bg-[#84CC16]',
          junkLead:         'bg-[#D946EF]',
        };
        return {
          label:      item.dispositionName,
          value:      fmt(item.count),
          percentage: item.percentage ?? 0,
          color:      colorMap[item.key] || 'bg-[#78909C]',
        };
      })
    : [];

  const pieData = conversion
    ? [
        { name: 'Order Placed',   count: conversion.distribution.orderPlaced?.count   ?? 0, percentage: conversion.distribution.orderPlaced?.percentage   ?? 0, color: '#10B981' },
        { name: 'New Leads',      count: conversion.distribution.newLeads?.count      ?? 0, percentage: conversion.distribution.newLeads?.percentage      ?? 0, color: '#F97316' },
        { name: 'Not Assigned',   count: conversion.distribution.notAssigned?.count   ?? 0, percentage: conversion.distribution.notAssigned?.percentage   ?? 0, color: '#FBBF24' },
        { name: 'Not Interested', count: conversion.distribution.notInterested?.count ?? 0, percentage: conversion.distribution.notInterested?.percentage ?? 0, color: '#EF4444' },
        { name: 'Not Connected',  count: conversion.distribution.notConnected?.count  ?? 0, percentage: conversion.distribution.notConnected?.percentage  ?? 0, color: '#8B5CF6' },
        { name: 'Call Back',      count: conversion.distribution.callBack?.count      ?? 0, percentage: conversion.distribution.callBack?.percentage      ?? 0, color: '#1E40AF' },
        { name: 'Other',          count: conversion.distribution.other?.count         ?? 0, percentage: conversion.distribution.other?.percentage         ?? 0, color: '#60A5FA' },
      ].map(d => ({ ...d, value: d.count > 0 ? d.count : 0.001 }))
    : [];

  const barData = bySource?.sources
    ? Object.entries(bySource.sources).map(([source, data]) => ({
        source,
        'Order Placed':  data.orderPlaced,
        'Not Converted': data.notConverted,
      }))
    : [];

  const totalLeads = summary?.totalLeads ?? 0;
  const hasFilters = activeFilters.startDate || activeFilters.endDate || activeFilters.floorId;

  // Active floor label for header badge
  const activeFloorLabel = floors.find(f => f.id === activeFilters.floorId)?.name ?? '';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-10 h-10 animate-spin text-[#26A69A]" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-800">Lead</h1>
          {activeFilters.floorId && (
            <span className="px-3 py-1 text-xs font-bold rounded-full bg-[#E3F2FD] text-[#1565C0] border border-[#90CAF9]">
              {activeFloorLabel}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { setTempFilters(activeFilters); setIsFilterOpen(true); }}
            className={`p-2 rounded-full border shadow-sm transition-all ${
              hasFilters
                ? 'bg-[#E3F2FD] border-[#2196F3] text-[#2196F3]'
                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-500'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            onClick={() => fetchAll(activeFilters)}
            className="p-2 bg-[#26A69A] rounded-full shadow-md hover:opacity-90 transition-opacity"
          >
            <RefreshCw size={20} className="text-white" />
          </button>
        </div>
      </div>

      {/* ── Main Stat Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {mainStats.map((s, i) => (
          <StatCard key={i} label={s.label} value={s.value} percentage={s.percentage} color={s.color} icon={s.icon} />
        ))}
      </div>

      {/* ── Status Breakdown Mini Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {detailedStats.map((s, i) => (
          <MiniStatCard key={i} label={s.label} value={s.value} percentage={s.percentage} color={s.color} />
        ))}
      </div>

      {/* ── Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Pie Chart */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col h-[550px]">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Lead and conversion summary</h2>
          <div className="flex-1 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%" cy="45%"
                  innerRadius={80} outerRadius={125}
                  paddingAngle={2} dataKey="value"
                  startAngle={90} endAngle={-270}
                  stroke="none"
                  label={({ cx, cy, midAngle, outerRadius, percentage, color }) => {
                    if (percentage < 3) return null;
                    const R = Math.PI / 180;
                    const r = outerRadius + 22;
                    const x = cx + r * Math.cos(-midAngle * R);
                    const y = cy + r * Math.sin(-midAngle * R);
                    return (
                      <text x={x} y={y} fill={color} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={700}>
                        {percentage}%
                      </text>
                    );
                  }}
                >
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                  formatter={(_, __, props) => [`${props.payload.name}: ${fmt(props.payload.count)}`, '']}
                />
                <text x="50%" y="43%" textAnchor="middle" dominantBaseline="middle" fontSize={14} fontWeight={900} fill="#64748B">Total Leads</text>
                <text x="50%" y="49%" textAnchor="middle" dominantBaseline="middle" fontSize={24} fontWeight={900} fill="#1f2937">{fmt(totalLeads)}</text>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center border-t border-gray-100 pt-3">
            {pieData.map((item, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                <span className="text-[10px] text-gray-500 font-semibold">{item.name}: {item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bar Chart */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col h-[550px]">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Lead received source wise</h2>
          <div className="flex items-center gap-5 justify-center mb-4">
            {[{ label: 'Order Placed', color: '#10B981' }, { label: 'Not Converted', color: '#EF4444' }].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: l.color }} />
                <span className="text-xs text-gray-500 font-bold">{l.label}</span>
              </div>
            ))}
          </div>
          <div className="flex-1">
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={barData} margin={{ top: 10, right: 10, left: -20, bottom: 50 }} barGap={4}>
                <XAxis dataKey="source" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 10, fill: '#64748B', fontWeight: 600 }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B', fontWeight: 600 }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                <Tooltip cursor={{ fill: 'rgba(239,246,255,0.4)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }} formatter={(v) => fmt(v)} />
                <Bar dataKey="Order Placed"  stackId="a" fill="#10B981" radius={[0,0,0,0]} barSize={40} />
                <Bar dataKey="Not Converted" stackId="a" fill="#EF4444" radius={[4,4,0,0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Funnel */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 flex flex-col h-[550px]">
          <h2 className="text-[17px] font-bold text-[#1f2937] mb-12">Lead Sales funnel</h2>
          <div className="flex-1 flex flex-col items-center justify-center space-y-1">
            <div className="w-full flex flex-col items-center">
              <div className="bg-[#94B8F2] w-full h-[55px] flex items-center justify-center text-white text-[11px] font-bold shadow-sm mb-1" style={{ clipPath: 'polygon(0% 0%, 100% 0%, 92% 100%, 8% 100%)' }}>
                LEADS: {fmt(breakdown?.totalLeads)} (100%)
              </div>
              <div className="bg-[#78A4EE] w-[84%] h-[50px] flex items-center justify-center text-white text-[11px] font-bold shadow-sm mb-1" style={{ clipPath: 'polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%)' }}>
                ORDERS: {fmt(breakdown?.orderPlaced)} ({((breakdown?.orderPlaced / breakdown?.totalLeads) * 100 || 0).toFixed(1)}%)
              </div>
              <div className="bg-[#568AEC] w-[67%] h-[45px] flex items-center justify-center text-white text-[11px] font-bold shadow-sm mb-1" style={{ clipPath: 'polygon(0% 0%, 100% 0%, 88% 100%, 12% 100%)' }}>
                VERIFIED: {fmt(orderSummary?.completeOrders)} ({((orderSummary?.completeOrders / breakdown?.totalLeads) * 100 || 0).toFixed(1)}%)
              </div>
              <div className="bg-[#2563EB] w-[50%] h-[40px] flex items-center justify-center text-white text-[11px] font-bold shadow-sm" style={{ clipPath: 'polygon(0% 0%, 100% 0%, 85% 100%, 15% 100%)' }}>
                DELIVERED: {fmt(orderSummary?.deliveredOrders)} ({((orderSummary?.deliveredOrders / breakdown?.totalLeads) * 100 || 0).toFixed(1)}%)
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Filter Modal ── */}
      {isFilterOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpen(false);
          }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl w-full max-w-md mx-4 overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-[#459B7A]">
              <h2 className="text-lg font-bold text-white">Filter Leads</h2>
              <button onClick={() => setIsFilterOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-5">

              {/* ── Floor selector — API se dynamic ── */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Floor
                  {floorsLoading && (
                    <span className="ml-2 inline-block w-3 h-3 border border-t-transparent border-teal-500 rounded-full animate-spin align-middle" />
                  )}
                </label>
                <select
                  value={tempFilters.floorId}
                  onChange={e => setTempFilters(f => ({ ...f, floorId: e.target.value }))}
                  disabled={floorsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer disabled:opacity-50"
                >
                  <option value="">
                    {floorsLoading ? 'Loading floors...' : 'All Floors'}
                  </option>
                  {floors.map(floor => (
                    <option key={floor.id} value={floor.id}>
                      {floor.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Date range ── */}
              <div>
                <DateRangeFilter
                  label="Date Range"
                  showPresets={false}
                  startDate={tempFilters.startDate}
                  endDate={tempFilters.endDate}
                  onChange={({ startDate, endDate }) =>
                    setTempFilters(f => ({
                      ...f,
                      startDate,
                      endDate,
                    }))
                  }
                />
              </div>

            </div>

            <div className="flex justify-end px-6 py-4 border-t bg-gray-50 gap-3">
              <button onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Reset
              </button>
              <button onClick={handleApply}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-teal-600 hover:bg-teal-700 transition-colors">
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLead;